import {
  isValidDate,
  parseUtcDate,
  parseWorkAvailabilityPriority,
  type IWorkAvailabilityWindow,
  type WorkAvailabilityPriorityValue,
  WorkAvailabilityPriority,
} from '@moko/utils';
import { defaultTimeZone } from '../../TimezoneSelect';
import { formatTimeOfDay, parseTimeOfDay } from '../../TimeSelect';

export type WorkAvailabilityFormValues = {
  date: Date | null;
  startTime: string;
  endTime: string;
  timezoneId: string;
  priority: WorkAvailabilityPriorityValue;
  sourceControlled: string;
  status: boolean;
  isAllDay: boolean;
  duration: number | null;
};

export const startOfDay = (value: Date) => {
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  return next;
};

const applyTime = (date: Date, time: string): Date | null => {
  const parsed = parseTimeOfDay(time);
  if (!parsed) return null;
  const next = new Date(date);
  next.setHours(parsed.getHours(), parsed.getMinutes(), parsed.getSeconds(), 0);
  return next;
};

export const emptyWorkAvailabilityForm = (): WorkAvailabilityFormValues => ({
  date: null,
  startTime: '',
  endTime: '',
  timezoneId: defaultTimeZone(),
  priority: WorkAvailabilityPriority.Preferred,
  sourceControlled: '',
  status: true,
  isAllDay: false,
  duration: null,
});

export const formFromWindow = (
  window: IWorkAvailabilityWindow
): WorkAvailabilityFormValues => {
  const startAt = window.localStartAt
    ? new Date(window.localStartAt)
    : window.startAt
    ? parseUtcDate(window.startAt)
    : null;
  const endAt = window.localEndAt
    ? new Date(window.localEndAt)
    : window.endAt
    ? parseUtcDate(window.endAt)
    : null;
  return {
    date: isValidDate(startAt) ? startOfDay(startAt) : null,
    startTime: isValidDate(startAt) ? formatTimeOfDay(startAt) : '',
    endTime: isValidDate(endAt) ? formatTimeOfDay(endAt) : '',
    timezoneId: window.timezoneId,
    priority:
      parseWorkAvailabilityPriority(window.priority) ??
      WorkAvailabilityPriority.Preferred,
    sourceControlled: window.sourceControlled ?? '',
    status: Boolean(window.status),
    isAllDay: Boolean(window.isAllDay),
    duration: window.duration ?? null,
  };
};

export const toWorkAvailabilityRange = (
  values: WorkAvailabilityFormValues
): { startAt: Date; endAt: Date } | null => {
  if (!isValidDate(values.date)) return null;
  const startAt = applyTime(
    values.date,
    values.isAllDay ? '00:00:00' : values.startTime
  );
  const endAt = applyTime(
    values.date,
    values.isAllDay ? '23:59:00' : values.endTime
  );
  if (!startAt || !endAt) return null;
  return { startAt, endAt };
};
